import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const SECTIONS = [
  {
    title: 'Information We Collect',
    content: [
      'Account credentials: your email address and chosen username, used for authentication and communications.',
      'Health & fitness data: step count, distance walked (in km), estimated calories burned, and active minutes — used to calculate DEEP Points and display your wellness statistics.',
      'Physical activity sensor data: your device\'s accelerometer and hardware step-detector sensor are used to count steps. This data is processed entirely on-device; only the aggregated daily total is stored.',
      'Location data: GPS coordinates are used only during active tracking sessions to calculate real-world walking distance. See the "Location Data — Detailed Disclosure" section below for complete details.',
      'Advertising identifiers: Google AdMob may collect your Android Advertising ID (GAID) to serve personalised or contextual advertisements. You can reset or opt out at any time in your device Settings → Google → Ads.',
      'Usage analytics: anonymised, aggregated crash reports and performance data to help us fix bugs and improve the app.',
      'Profile preferences: display name, notification settings, and other preferences you configure in the app.',
    ]
  },
  {
    title: 'Location Data — Detailed Disclosure',
    highlight: true,
    content: [
      '⚠️  PROMINENT DISCLOSURE — Please read this section carefully before granting location permissions.',
      'DEEP Health requests three location permissions: ACCESS_FINE_LOCATION (GPS-level precision), ACCESS_COARSE_LOCATION (network-level precision), and ACCESS_BACKGROUND_LOCATION.',
      'Fine (GPS) location is used to record accurate walking and running distances during an active workout session.',
      'Coarse location is used as a battery-efficient fallback when GPS is unavailable, providing an approximate position sufficient for distance estimation.',
      'Background location allows our foreground step-tracker service to continue counting steps and logging your route when you minimise the app or lock your screen mid-workout. A visible "DEEP Health Tracker" notification with a live step-count progress bar is always displayed while the service is running — you will always know it is active.',
      'You can stop the service at any time by tapping "Stop" in the notification or inside the app.',
      'What we store: only the total distance in km for each session. Raw GPS coordinates are processed in real time on your device and are NEVER uploaded to our servers.',
      'What we NEVER do: we never use your location for advertising, profiling, or any purpose other than distance calculation, and we never share raw location data with any third party.',
      'How to revoke access: Settings → Apps → DEEP Health → Permissions → Location. You can switch to "Only while using the app" or "Deny" at any time. Revoking location disables distance tracking but does not affect step counting.',
    ]
  },
  {
    title: 'Physical Activity & Sensor Data',
    content: [
      'We request the ACTIVITY_RECOGNITION permission to access your device\'s hardware step-detector and accelerometer sensors.',
      'Sensor readings are processed entirely on your device in real time. Raw accelerometer values are never stored or transmitted.',
      'Only the final step count total for each day is saved to your Supabase-hosted account.',
      'This data is used solely to display wellness statistics and calculate DEEP Points — never for any other purpose.',
    ]
  },
  {
    title: 'How We Use Your Information',
    content: [
      'To provide core app features: counting steps, calculating distance, displaying health statistics, and awarding DEEP Points.',
      'To authenticate your account and send essential security or service-related notifications.',
      'To display advertisements via Google AdMob, which keeps the app free for all users.',
      'To improve app performance and fix issues using anonymised, aggregated usage data.',
      'We do NOT sell, rent, or trade your personal data to any third party for marketing or any other commercial purpose.',
    ]
  },
  {
    title: 'DEEP Points — Important Disclaimer',
    content: [
      'DEEP Points are virtual in-app rewards with absolutely no monetary value and no guaranteed cash value.',
      'Points cannot currently be exchanged for money, cryptocurrency, or any real-world asset.',
      'We do not charge users any money to earn or use DEEP Points. No purchase is ever required.',
      'Accumulating DEEP Points creates no legal entitlement to any form of compensation or financial benefit.',
      'Any future $DEEP token launch or conversion programme is entirely at the discretion of the company and is not guaranteed.',
      'Do not make financial decisions based on anticipated point value.',
    ]
  },
  {
    title: 'Advertising (Google AdMob)',
    content: [
      'We use Google AdMob to display in-app advertisements that keep the app free for users.',
      'AdMob may collect your Advertising ID and contextual usage signals to serve relevant ads.',
      'You can review and control Google\'s data practices at https://policies.google.com/privacy.',
      'You can opt out of personalised ads in your device Settings → Google → Ads → Opt out of Ads Personalisation.',
      'We do not have direct access to personally identifiable information collected by AdMob.',
      'Watching rewarded video ads to earn bonus DEEP Points is entirely voluntary.',
    ]
  },
  {
    title: 'Data Storage & Security',
    content: [
      'All user data is stored securely via Supabase using AES-256 encryption at rest and TLS 1.3 in transit.',
      'We retain your account data for as long as your account remains active.',
      'You may request deletion of your account and all associated data at any time via Settings → Contact / Report.',
      'We apply industry-standard security controls to prevent unauthorised access, disclosure, or alteration of your data.',
    ]
  },
  {
    title: 'Data Safety (Google Play)',
    content: [
      'In line with Google Play\'s Data Safety requirements, we declare the following:',
      'Data collected: email address, step count, walking distance (km), calories, approximate location (during sessions only).',
      'Data shared with third parties: advertising identifiers shared with Google AdMob for ad serving only.',
      'All data is encrypted in transit (TLS 1.3) and at rest (AES-256).',
      'You can request deletion of all your data at any time in-app or by contacting us.',
      'Location data is NOT used for advertising and is NOT sold to any third party.',
    ]
  },
  {
    title: 'Your Rights',
    content: [
      'You have the right to access, correct, or delete your personal data at any time.',
      'You may withdraw consent for location tracking by revoking location permissions in your device settings.',
      'You may request a copy of all data we hold about you via the in-app support feature.',
      'You may delete your account and all associated data in Settings → Delete Account.',
      'EU/EEA users have additional rights under GDPR including data portability and the right to lodge a complaint with your national supervisory authority.',
      'California users have rights under the CCPA including the right to know what data we collect and the right to opt out of sale of personal information (we do not sell personal data).',
    ]
  },
  {
    title: "Children's Privacy",
    content: [
      'DEEP Health is not intended for users under 13 years of age.',
      'We do not knowingly collect personal data from children under 13.',
      'Users aged 13–17 must have parental or guardian consent before using the app.',
      'If you believe a child under 13 has created an account, please contact us immediately and we will permanently delete the account and all associated data.',
    ]
  },
  {
    title: 'Third-Party Services',
    content: [
      'Supabase — database, authentication, and encrypted storage. Policy: https://supabase.com/privacy',
      'Google AdMob — in-app advertising. Policy: https://policies.google.com/privacy',
      'Google Play Services — app distribution and device services.',
      'Each third-party service operates under its own privacy policy and data practices.',
    ]
  },
  {
    title: 'Changes to This Policy',
    content: [
      'We may update this Privacy Policy to reflect changes in our practices or applicable law.',
      'We will notify you of material changes via an in-app notification before they take effect.',
      'The "Last updated" date at the top reflects the most recent revision.',
      'Continued use of the app after an updated policy is posted constitutes acceptance of the changes.',
    ]
  },
  {
    title: 'Contact Us',
    content: [
      'For questions, requests, or concerns about this Privacy Policy or your personal data, please use the Report / Contact feature in the app Settings.',
      'We aim to respond to all privacy-related enquiries within 30 days.',
    ]
  },
];

export default function Privacy() {
  return (
    <main className="legal-page">
      <div className="legal-hero">
        <div className="legal-hero-bg" />
        <div className="container">
          <div className="legal-label">Legal Document</div>
          <h1 className="legal-title">Privacy <span className="gradient-text">Policy</span></h1>
          <p className="legal-date">Last updated: 22 March 2026 — DEEP Health Tracker</p>
          <p className="legal-intro">
            This Privacy Policy explains how DEEP Health — Earn &amp; Track ("we", "our", "the App") collects,
            uses, and protects your personal information. We are fully committed to transparency and
            compliance with applicable privacy laws including GDPR, CCPA, and Google Play policies.
          </p>
        </div>
      </div>

      <div className="container legal-content">
        <div className="legal-warning">
          <div className="legal-warning-icon">⚠️</div>
          <div>
            <p className="legal-warning-title">Points Disclaimer</p>
            <p className="legal-warning-body">
              DEEP Points are virtual in-app rewards with no monetary value. We never charge you to use
              the app or earn points, and we make no financial guarantees of any kind.
            </p>
          </div>
        </div>

        <div className="legal-location-box">
          <div className="legal-location-icon">📍</div>
          <div>
            <p className="legal-location-title">Background Location — Why We Need It</p>
            <p className="legal-location-body">
              DEEP Health uses background location <strong>only</strong> to continue tracking your walking
              distance when the app is minimised mid-workout. A visible persistent notification is always
              shown while tracking is active. Raw GPS coordinates are <strong>never</strong> stored or
              uploaded — only your total distance in km is saved. Location is <strong>never</strong> used
              for advertising. You can stop tracking anytime from the notification or inside the app.
            </p>
          </div>
        </div>

        {SECTIONS.map((section, i) => (
          <div key={i} className={`legal-section${section.highlight ? ' legal-section--highlight' : ''}`}>
            <div className="legal-section-num">{String(i + 1).padStart(2, '0')}</div>
            <div className="legal-section-body">
              <h2 className="legal-section-title">{section.title}</h2>
              <ul className="legal-list">
                {section.content.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}

        <div className="legal-footer-note">
          <p>
            By using DEEP Health, you confirm you have read and agree to this Privacy Policy and our{' '}
            <Link to="/terms">Terms &amp; Conditions</Link>.
          </p>
        </div>
      </div>
    </main>
  );
}
